import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CategoryFilter } from "@/components/Home/CategoryFilter";
import { ModernPromptFeed } from "@/components/Feed/ModernPromptFeed";
import { Navbar } from "@/components/Layout/Navbar";
import { Footer } from "@/components/Layout/Footer";
import { Category, SortOption, GeoFilters } from "@/lib/types";
import { GeoFiltersBar } from "@/components/GEO/GeoFiltersBar";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import CosmicBackground from "@/components/CosmicBackground";
import { motion } from "framer-motion";
import { Zap } from "lucide-react";

const Prompts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");
  const [selectedSort, setSelectedSort] = useState<SortOption>("latest");
  const [geoFilters, setGeoFilters] = useState<GeoFilters>({});
  const location = useLocation();
  const newPromptId = location.state?.newPromptId;

  useEffect(() => {
    if (newPromptId) {
      setTimeout(() => {
        const promptElement = document.querySelector(`[data-prompt-id="${newPromptId}"]`);
        if (promptElement) {
          promptElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [newPromptId]);

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Cosmic Background */}
      <CosmicBackground />
      
      <Navbar />
      
      <main className="flex-1 pt-16 relative z-10">
        <div className="container mx-auto px-3 md:px-4 py-6 md:py-8 max-w-6xl">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-12"
          >
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              <span className="text-white">Explore </span>
              <span className="text-gradient-standard">Prompts</span>
            </h1>
            <p className="font-body text-base md:text-lg text-white/60 max-w-2xl mx-auto leading-relaxed px-2">
              Discover AI prompts crafted by creators worldwide. Find inspiration, remix ideas, and build your perfect prompt library.
            </p>
          </motion.div>

          {!user ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col items-center justify-center min-h-[50vh] text-center space-y-6 p-8 glass-card max-w-lg mx-auto"
            >
              <div className="w-16 h-16 rounded-full bg-brand-cyan/20 flex items-center justify-center mb-2">
                <Zap className="h-8 w-8 text-brand-cyan" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-white">
                Welcome to ThePromptSpace
              </h2>
              <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
                Sign in to create, comment, and save prompts. Browsing is always open.
              </p>
              <Button
                onClick={() => navigate("/auth")}
                className="bg-gradient-to-r from-brand-purple via-brand-pink to-brand-cyan text-white rounded-xl px-8 py-6 text-base font-semibold shadow-lg hover:shadow-[0_0_30px_rgba(112,0,255,0.4)] transition-all duration-300"
              >
                Sign In
              </Button>
            </motion.div>
          ) : (
            <>
              {/* Category Filter */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="mb-4 md:mb-6"
              >
                <CategoryFilter
                  selectedCategory={selectedCategory}
                  onSelectCategory={setSelectedCategory}
                  selectedSort={selectedSort}
                  onSelectSort={setSelectedSort}
                />
              </motion.div>

              {/* GEO Filters */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                className="mb-6 md:mb-8"
              >
                <GeoFiltersBar
                  filters={geoFilters}
                  onFiltersChange={setGeoFilters}
                />
              </motion.div>

              {/* Prompt Feed */}
              <ModernPromptFeed
                selectedCategory={selectedCategory}
                selectedSort={selectedSort}
                geoFilters={geoFilters}
              />
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Prompts;
